import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ImageUploaderComponent } from '../../components/image-uploader/image-uploader.component';
import { ProductService } from '../../core/services/product.service';
import { CategoryService } from '../../core/services/category.service';
import { CollectionService } from '../../core/services/collection.service';
import { DeviceModelService } from '../../core/services/device-model.service';
import { MaterialService } from '../../core/services/material.service';
import { BrandService } from '../../core/services/brand.service';
import { BrandGroupService } from '../../core/services/brand-group.service';
import { UploadService } from '../../core/services/upload.service';
import { Product, ProductImage, ProductFrame } from '../../models/product.interface';
import { Category } from '../../models/category.interface';
import { Collection } from '../../models/collection.interface';
import { SubCategory } from '../../models/sub-category.interface';
import { SubCategoryService } from '../../core/services/sub-category.service';
import { DeviceModelItem } from '../../models/device-model.interface';
import { MaterialItem } from '../../models/material.interface';

import { BrandItem } from '../../models/brand.interface';
import { BrandGroup } from '../../models/brand-group.interface';

const MOBILE_CATEGORY_SLUGS = ['mobile-cases', 'mobile cases', 'phone-cases', 'phone cases'];

@Component({
  selector: 'app-product-form',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, SidebarComponent, NavbarComponent, ImageUploaderComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar 
      [pageTitle]="isEditMode ? 'Edit Product' : 'Add New Product'" 
      [pageSubtitle]="isEditMode ? 'Update design specs & images' : 'Create new luxury case design in catalog'"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">
        <div class="form-card glass-card">
          <div class="form-header">
            <h2 class="font-serif title-gold">{{ isEditMode ? 'Edit Case Specs' : 'New Design Details' }}</h2>
            <a routerLink="/products" class="btn-secondary">← Back to Catalog</a>
          </div>

        <form (ngSubmit)="onSubmit()" class="product-form">
          <div class="form-grid">
            <!-- LEFT COLUMN: PRODUCT DETAILS -->
            <div class="form-column">
              <div class="form-group">
                <label class="form-label">Product Title *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="product.title" 
                  name="title" 
                  placeholder="e.g. 911 Rsr Lime Phone Case Cover" 
                  required 
                />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Selling Price (₹) *</label>
                  <input 
                    type="number" 
                    class="form-control" 
                    [(ngModel)]="product.price" 
                    name="price" 
                    placeholder="555" 
                    required 
                  />
                </div>

                <div class="form-group">
                  <label class="form-label">Original / Slash Price (₹)</label>
                  <input 
                    type="number" 
                    class="form-control" 
                    [(ngModel)]="product.slashPrice" 
                    name="slashPrice" 
                    placeholder="666" 
                  />
                </div>
              </div>

              <!-- CATEGORY + COLLECTION + SUB-CATEGORY -->
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">
                    Category *
                    <span *ngIf="product.category" class="badge" [class.badge-gold]="isMobileCategory" [class.badge-secondary]="!isMobileCategory" style="font-size:10px; margin-left:6px;">
                      {{ isMobileCategory ? '📱 Mobile' : '💻 Non-Mobile' }}
                    </span>
                  </label>
                  <select class="form-control" [(ngModel)]="product.category" name="category" required (ngModelChange)="onCategoryChange($event)">
                    <option value="">Select Category</option>
                    <option *ngFor="let cat of categories" [value]="cat.slug">
                      {{ cat.name }} {{ cat.isMobile !== false ? '(Mobile Cases)' : '(Non-Mobile)' }}
                    </option>
                  </select>
                </div>

                <!-- 2. SUB-CATEGORY (Level 2) -->
                <div class="form-group">
                  <label class="form-label">
                    Sub-Category (Optional)
                    <span *ngIf="availableSubCategories.length > 0" class="sub-text" style="font-size:11px; margin-left:4px;">
                      ({{ availableSubCategories.length }} available)
                    </span>
                  </label>
                  <select class="form-control" [(ngModel)]="product.subCategory" name="subCategory" (ngModelChange)="onSubCategoryChange($event)">
                    <option value="">Select Sub-Category (Optional)</option>
                    <option *ngFor="let sub of availableSubCategories" [value]="sub.name">
                      {{ sub.name }}
                    </option>
                  </select>
                  <div *ngIf="product.category && availableSubCategories.length === 0" style="margin-top:4px;">
                    <a routerLink="/subcategories" [queryParams]="{ category: product.category }" class="sub-text" style="color:var(--accent-gold); font-size:11px; text-decoration:underline;">
                      + No sub-categories yet for this category. Click to add one →
                    </a>
                  </div>
                </div>

                <!-- 3. COLLECTION (Level 3) -->
                <div class="form-group">
                  <label class="form-label">
                    Collection (Optional)
                    <span *ngIf="availableCollections.length > 0" class="sub-text" style="font-size:11px; margin-left:4px;">
                      ({{ availableCollections.length }} available)
                    </span>
                  </label>
                  <select class="form-control" [(ngModel)]="product.collectionName" name="collectionName">
                    <option value="">Select Collection (Optional)</option>
                    <option *ngFor="let col of (availableCollections.length > 0 ? availableCollections : collections)" [value]="col.slug">
                      {{ col.name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- BRAND GROUPS (MULTI-SELECT, Mobile only) -->
              <div class="form-group" *ngIf="isMobileCategory">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <label class="form-label" style="margin-bottom: 0;">
                    📱 Model Groups (Multi-Select)
                    <span class="form-hint">Assign one or more device model groups</span>
                  </label>
                  <button type="button" class="btn-text-action" *ngIf="(product.brandGroups || []).length > 0" (click)="clearAllBrandGroups()">Clear Groups</button>
                </div>

                <div class="groups-selector-grid">
                  <label
                    *ngFor="let grp of brandGroups"
                    class="group-select-card"
                    [class.is-selected]="isBrandGroupSelected(grp.name)"
                  >
                    <input
                      type="checkbox"
                      [checked]="isBrandGroupSelected(grp.name)"
                      (change)="toggleBrandGroup(grp.name)"
                    />
                    <div class="grp-card-body">
                      <span class="grp-card-title">{{ grp.name }}</span>
                      <small *ngIf="grp.displayLabel" class="grp-card-subtitle">{{ grp.displayLabel }}</small>
                      <span class="grp-card-count">{{ grp.deviceModels.length }} models</span>
                    </div>
                  </label>
                </div>

                <!-- Preview selected group models -->
                <div class="selected-groups-preview" *ngIf="(product.brandGroups || []).length > 0">
                  <span class="sub-text" style="font-size:11px;">Selected Groups: </span>
                  <span *ngFor="let g of product.brandGroups" class="model-chip-sm" style="border-color: var(--accent-gold); color: var(--accent-gold); margin-right: 4px;">
                    {{ g }}
                  </span>
                  <span class="sub-text" style="font-size:11px; margin-left: 8px;">(Total Models: {{ (product.deviceModels || []).length }})</span>
                </div>
              </div>

              <!-- PRODUCT TYPE & SIZE PRICING -->
              <div class="form-group">
                <label class="form-label">Product Type</label>
                <select class="form-control" [(ngModel)]="product.productType" name="productType">
                  <option value="">Auto (from category)</option>
                  <option value="phone-case">Phone case (customer picks device brand &amp; model)</option>
                  <option value="poster">Art poster / wall art (sizes &amp; frames)</option>
                  <option value="desk-mat">Desk mat (sizes)</option>
                  <option value="other">Other product: mug, T-shirt, laptop sleeve, keychain... (sizes optional)</option>
                </select>
                <small class="sub-text">
                  Only Phone case asks for a device. Auto uses the category: Mobile categories become Phone case, Non-Mobile ones become Other.
                </small>
              </div>

              <div class="form-group" *ngIf="supportsSizeOptions && product.productType === 'other'">
                <label class="form-label">Wording on the product page (optional)</label>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                  <input type="text" class="form-control" [(ngModel)]="product.typeLabel" name="typeLabel" maxlength="40" placeholder="Product name, e.g. T-Shirt, Mug, Laptop Sleeve" />
                  <input type="text" class="form-control" [(ngModel)]="product.optionLabel" name="optionLabel" maxlength="40" placeholder="Swatch group, e.g. Color, Finish, Frame" />
                </div>
                <small class="sub-text">Shown as "Select T-Shirt size" and a "Color" swatch row. Leave empty for "Product" / "Frame". Products with a single price need no sizes.</small>
              </div>

              <small class="sub-text" *ngIf="!supportsSizeOptions" style="display:block; margin-bottom: 12px;">
                Sizes and frames are available when Product Type is Art poster, Desk mat or Other.
              </small>

              <div class="form-group" *ngIf="supportsSizeOptions">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <label class="form-label" style="margin-bottom: 0;">Sizes &amp; Variable Pricing</label>
                  <button type="button" class="btn-text-action" (click)="addVariant()">+ Add size</button>
                </div>
                <small class="sub-text" *ngIf="!(product.variants && product.variants.length)">
                  No sizes added: the product uses the single Selling Price above. Add sizes (e.g. A3, A4) to give each its own price.
                </small>
                <div *ngFor="let v of product.variants; let i = index; trackBy: trackByIndex"
                     style="display: grid; grid-template-columns: 2fr 1fr 1fr 2fr auto auto; gap: 8px; align-items: center; margin-bottom: 8px;">
                  <input type="text" class="form-control" [(ngModel)]="v.name" [name]="'variantName' + i" placeholder="Size (e.g. A3)" />
                  <input type="number" class="form-control" [(ngModel)]="v.price" [name]="'variantPrice' + i" placeholder="Price ₹" min="1" />
                  <input type="number" class="form-control" [(ngModel)]="v.slashPrice" [name]="'variantSlash' + i" placeholder="MRP ₹" min="0" />
                  <input type="text" class="form-control" [(ngModel)]="v.specs" [name]="'variantSpecs' + i" placeholder="Details (e.g. 297 x 420 mm)" />
                  <label style="display: flex; align-items: center; gap: 4px; font-size: 12px; white-space: nowrap;">
                    <input type="checkbox" [(ngModel)]="v.inStock" [name]="'variantStock' + i" /> In stock
                  </label>
                  <button type="button" class="btn-text-action" (click)="removeVariant(i)" aria-label="Remove size">✕</button>
                </div>
                <small class="sub-text" *ngIf="product.variants && product.variants.length">
                  The product's Selling Price is set automatically to the cheapest in-list size ("From ₹…" on listings).
                </small>
              </div>

              <!-- FRAME OPTIONS (posters) -->
              <div class="form-group" *ngIf="supportsSizeOptions">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <label class="form-label" style="margin-bottom: 0;">Frame Options (optional)</label>
                  <button type="button" class="btn-text-action" (click)="addFrame()">+ Add frame</button>
                </div>
                <small class="sub-text" *ngIf="!(product.frames && product.frames.length)">
                  Leave empty if the product has no frame choice. Each frame's "Extra price" is added to the selected size's price (use 0 for the cheapest option, e.g. No Frame).
                </small>
                <div *ngFor="let f of product.frames; let i = index; trackBy: trackByIndex"
                     style="display: grid; grid-template-columns: 2fr 1fr auto auto auto; gap: 8px; align-items: center; margin-bottom: 8px;">
                  <input type="text" class="form-control" [(ngModel)]="f.name" [name]="'frameName' + i" placeholder="Frame (e.g. Black)" />
                  <input type="number" class="form-control" [(ngModel)]="f.priceAdjustment" [name]="'frameAdj' + i" placeholder="Extra price ₹" min="0" />
                  <label style="display: flex; align-items: center; gap: 6px; font-size: 12px; white-space: nowrap; cursor: pointer;">
                    <img *ngIf="f.imageUrl" [src]="f.imageUrl" alt="" style="width: 32px; height: 32px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px;" />
                    <span class="btn-text-action">{{ f.imageUrl ? 'Change image' : 'Upload image' }}</span>
                    <input type="file" accept="image/*" style="display: none;" (change)="onFrameImageSelected($event, f)" />
                  </label>
                  <label style="display: flex; align-items: center; gap: 4px; font-size: 12px; white-space: nowrap;">
                    <input type="checkbox" [(ngModel)]="f.inStock" [name]="'frameStock' + i" /> In stock
                  </label>
                  <button type="button" class="btn-text-action" (click)="removeFrame(i)" aria-label="Remove frame">✕</button>
                </div>
              </div>

              <!-- CASE MATERIALS (MULTI-SELECT) -->
              <div class="form-group">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                  <label class="form-label" style="margin-bottom: 0;">Case Materials * (Multi-Select)</label>
                  <div style="display: flex; gap: 8px;">
                    <button type="button" class="btn-text-action" (click)="selectAllMaterials()">Select All</button>
                    <button type="button" class="btn-text-action" (click)="clearAllMaterials()">Clear</button>
                  </div>
                </div>

                <div class="materials-selector-grid">
                  <label 
                    *ngFor="let mat of materials" 
                    class="material-select-card"
                    [class.is-selected]="isMaterialSelected(mat.name)"
                  >
                    <input 
                      type="checkbox" 
                      [checked]="isMaterialSelected(mat.name)" 
                      (change)="toggleMaterial(mat.name)"
                    />
                    <div class="mat-card-body">
                      <span class="mat-card-title">{{ mat.name }}</span>
                      <small *ngIf="mat.description" class="mat-card-subtitle">{{ mat.description }}</small>
                    </div>
                  </label>
                </div>

                <div class="selected-materials-preview" *ngIf="product.materials && product.materials.length > 0">
                  <span class="sub-text" style="font-size: 11px;">Selected Materials ({{ product.materials.length }}): </span>
                  <span *ngFor="let m of product.materials" class="model-chip-sm">
                    {{ m }}
                  </span>
                </div>
              </div>

              <!-- BADGE TAG -->
              <div class="form-group">
                <label class="form-label">Badge Tag</label>
                <input 
                  type="text" 
                  class="form-control" 
                  [(ngModel)]="product.badge" 
                  name="badge" 
                  placeholder="Sale, Sparkle, Bestseller, Trending" 
                />
              </div>

              <!-- DEVICE MODELS (manual, shown when no group selected or not mobile) -->
              <div class="form-group" *ngIf="!product.brandGroup || !isMobileCategory">
                <label class="form-label">Device Model Compatibility</label>
                <div class="device-checkboxes-grid">
                  <label *ngFor="let dev of deviceModels" class="checkbox-label">
                    <input 
                      type="checkbox" 
                      [checked]="isDeviceModelSelected(dev.name)" 
                      (change)="toggleDeviceModel(dev.name)"
                    />
                    <span>{{ dev.name }}</span>
                  </label>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Series Label</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    [(ngModel)]="product.label" 
                    name="label" 
                    placeholder="e.g. 911 RSR or GT3 RS" 
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">Icon / Emoji</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    [(ngModel)]="product.icon" 
                    name="icon" 
                    placeholder="🏎️" 
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">Description</label>
                <textarea 
                  class="form-control" 
                  rows="3" 
                  [(ngModel)]="product.description" 
                  name="description" 
                  placeholder="Luxury handcrafted UV printed armor phone case specs..."
                ></textarea>
              </div>

              <div class="form-group checkboxes-group">
                <label class="checkbox-label">
                  <input type="checkbox" [(ngModel)]="product.inStock" name="inStock" />
                  <span>In-Stock Available for Purchase</span>
                </label>

                <label class="checkbox-label">
                  <input type="checkbox" [(ngModel)]="product.featured" name="featured" />
                  <span>Featured Drop on Homepage</span>
                </label>
              </div>
            </div>

            <!-- RIGHT COLUMN: IMAGE UPLOADS -->
            <div class="form-column">

              <!-- MULTI-IMAGE UPLOAD (up to 6) -->
              <div class="form-group">
                <label class="form-label">
                  Product Images (up to 6)
                  <span class="form-hint">First image is primary. Drag to reorder.</span>
                </label>

                <div class="multi-image-grid">
                  <!-- Uploaded images -->
                  <div 
                    *ngFor="let img of (product.images || []); let i = index" 
                    class="image-slot image-slot-filled"
                    [class.image-slot-primary]="i === 0"
                  >
                    <img [src]="img.url" [alt]="'Product image ' + (i+1)" />
                    <div class="image-slot-overlay">
                      <span *ngIf="i === 0" class="primary-badge">Primary</span>
                      <button type="button" class="remove-img" (click)="removeProductImage(i)">×</button>
                    </div>
                  </div>

                  <!-- Add image slot -->
                  <div 
                    *ngIf="(product.images || []).length < 6"
                    class="image-slot image-slot-add"
                    [class.uploading]="uploadingImages"
                    (click)="!uploadingImages && multiImageInput.click()"
                  >
                    <div *ngIf="!uploadingImages">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color: var(--text-muted)">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      <span>Add Image</span>
                    </div>
                    <div class="upload-spinner" *ngIf="uploadingImages"></div>
                    <input 
                      #multiImageInput
                      type="file" 
                      accept="image/*" 
                      multiple
                      style="display:none"
                      (change)="onMultiImageSelected($event)"
                    />
                  </div>
                </div>

                <p class="sub-text" style="font-size: 11px; margin-top: 8px;">
                  {{ (product.images || []).length }}/6 images · JPG, PNG, WebP supported · Max 5MB each
                </p>
              </div>

              <!-- LEGACY SINGLE IMAGE (Cloudinary component) -->
              <div class="form-group">
                <label class="form-label">Legacy Single Image (Cloudinary)</label>
                <app-image-uploader
                  [currentImageUrl]="product.imageUrl || ''"
                  [cloudinaryId]="product.cloudinaryId || ''"
                  (imageUploaded)="onImageUploaded($event)"
                  (imageRemoved)="onImageRemoved()"
                ></app-image-uploader>
              </div>

              <!-- Live Preview Card -->
              <div class="preview-card-box">
                <span class="sub-text">Live Admin Preview</span>
                <div class="live-product-preview" [style.background]="product.bg || '#18181B'">
                  <span class="badge-tag">{{ product.badge || 'Sale' }}</span>
                  <div class="preview-img" *ngIf="product.imageUrl || (product.images && product.images.length > 0)">
                    <img [src]="product.imageUrl || product.images?.[0]?.url" alt="preview" />
                  </div>
                  <div class="preview-content" *ngIf="!product.imageUrl && !(product.images && product.images.length > 0)">
                    <span class="icon">{{ product.icon || '📱' }}</span>
                    <strong class="label">{{ product.label || 'SHELL MUZE' }}</strong>
                  </div>
                </div>
              </div>

              <!-- BG Color -->
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Card BG Color</label>
                  <div class="color-input-row">
                    <input type="color" [(ngModel)]="product.bg" name="bg" style="width:40px;height:36px;border:none;background:none;cursor:pointer;" />
                    <input type="text" class="form-control" [(ngModel)]="product.bg" name="bgHex" placeholder="#18181B" />
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div class="form-footer">
            <button type="button" routerLink="/products" class="btn-secondary">Cancel</button>
            <button type="submit" class="btn-gold" [disabled]="saving">
              <span *ngIf="!saving">💾 {{ isEditMode ? 'Update Product' : 'Save Product' }}</span>
              <span *ngIf="saving">Saving to Catalog...</span>
            </button>
          </div>
        </form>
      </div>
    </main>
  </div>
  `,
  styles: [`
    .form-card {
      padding: 32px;
      max-width: 1100px;
      margin: 0 auto;
    }

    .form-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 28px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;
    }

    @media (max-width: 900px) {
      .form-grid { grid-template-columns: 1fr; }
    }

    .form-column {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .form-row {
      display: flex;
      gap: 16px;
    }

    .form-row .form-group { flex: 1; }

    .form-hint {
      font-size: 10.5px;
      color: var(--text-muted);
      margin-left: 8px;
      font-weight: 400;
    }

    /* Device checkboxes */
    .device-checkboxes-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      padding: 10px;
      background: rgba(0,0,0,0.2);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      max-height: 140px;
      overflow-y: auto;
    }

    .checkboxes-group {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 8px;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: var(--text-primary);
      cursor: pointer;
    }

    .checkbox-label input {
      width: 16px;
      height: 16px;
      accent-color: var(--accent-gold);
    }

    /* Materials Multi-Select Grid */
    .btn-text-action {
      background: none;
      border: none;
      color: var(--accent-gold);
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      padding: 0 4px;
    }
    .btn-text-action:hover {
      text-decoration: underline;
    }

    .materials-selector-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      gap: 10px;
      margin-bottom: 8px;
    }

    .material-select-card {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 14px;
      background: rgba(0, 0, 0, 0.2);
      border: 1.5px solid var(--border-color);
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all 0.2s;
    }

    .material-select-card:hover {
      border-color: rgba(201, 169, 110, 0.5);
    }

    .material-select-card.is-selected {
      border-color: var(--accent-gold);
      background: rgba(201, 169, 110, 0.08);
    }

    .material-select-card input[type="checkbox"] {
      margin-top: 3px;
      width: 16px;
      height: 16px;
      accent-color: var(--accent-gold);
      cursor: pointer;
    }

    .mat-card-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .mat-card-title {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--text-primary);
    }

    .mat-card-subtitle {
      font-size: 10.5px;
      color: var(--text-muted);
      line-height: 1.3;
    }

    .selected-materials-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
      margin-top: 6px;
    }

    /* Groups Multi-Select Grid */
    .groups-selector-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 10px;
      margin-bottom: 8px;
    }

    .group-select-card {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 12px;
      background: rgba(0, 0, 0, 0.2);
      border: 1.5px solid var(--border-color);
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all 0.2s;
    }

    .group-select-card:hover {
      border-color: rgba(201, 169, 110, 0.5);
    }

    .group-select-card.is-selected {
      border-color: var(--accent-gold);
      background: rgba(201, 169, 110, 0.08);
    }

    .group-select-card input[type="checkbox"] {
      margin-top: 3px;
      width: 16px;
      height: 16px;
      accent-color: var(--accent-gold);
      cursor: pointer;
    }

    .grp-card-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .grp-card-title {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--text-primary);
    }

    .grp-card-subtitle {
      font-size: 10.5px;
      color: var(--text-muted);
      line-height: 1.3;
    }

    .grp-card-count {
      font-size: 10px;
      color: var(--accent-gold);
      margin-top: 2px;
    }

    .selected-groups-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
      margin-top: 6px;
    }

    /* Brand Group Preview */
    .group-models-preview {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
      margin-top: 8px;
    }

    .model-chip-sm {
      display: inline-block;
      padding: 2px 8px;
      background: rgba(255,255,255,0.05);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-full);
      font-size: 10px;
      color: var(--text-secondary);
    }

    /* Multi-image grid */
    .multi-image-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
    }

    .image-slot {
      aspect-ratio: 1;
      border-radius: var(--radius-md);
      overflow: hidden;
      position: relative;
      border: 1.5px solid var(--border-color);
      transition: all 0.2s;
    }

    .image-slot-filled img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .image-slot-filled:hover .image-slot-overlay {
      opacity: 1;
    }

    .image-slot-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding: 6px;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .image-slot-primary {
      border-color: var(--accent-gold);
    }

    .image-slot-primary .image-slot-overlay {
      opacity: 1;
      background: transparent;
    }

    .primary-badge {
      font-size: 9px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      background: var(--accent-gold);
      color: #0B0F17;
      padding: 2px 6px;
      border-radius: 4px;
    }

    .remove-img {
      background: rgba(0,0,0,0.7);
      color: #fff;
      border: none;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      cursor: pointer;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .image-slot-add {
      border: 2px dashed var(--border-color);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      cursor: pointer;
      font-size: 11px;
      color: var(--text-muted);
      transition: all 0.2s;
    }

    .image-slot-add:hover {
      border-color: var(--accent-gold);
      color: var(--accent-gold);
      background: var(--accent-gold-glow);
    }

    .image-slot-add.uploading {
      cursor: not-allowed;
    }

    .upload-spinner {
      width: 24px;
      height: 24px;
      border: 2px solid var(--border-color);
      border-top-color: var(--accent-gold);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin { to { transform: rotate(360deg); } }

    /* Color input */
    .color-input-row {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    /* Preview Card */
    .preview-card-box {
      margin-top: 4px;
      padding: 16px;
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      background: rgba(0,0,0,0.2);
    }

    .live-product-preview {
      height: 140px;
      border-radius: var(--radius-md);
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      color: #FFF;
      box-shadow: inset 0 0 20px rgba(0,0,0,0.4);
      overflow: hidden;
    }

    .preview-img {
      width: 100%;
      height: 100%;
      position: absolute;
      inset: 0;
    }

    .preview-img img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .badge-tag {
      position: absolute;
      top: 10px;
      left: 10px;
      background: #111;
      color: #FFF;
      padding: 3px 8px;
      border-radius: 99px;
      font-size: 10px;
      font-weight: 700;
      z-index: 1;
    }

    .preview-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    .preview-content .icon { font-size: 24px; }
    .preview-content .label { font-size: 12px; letter-spacing: 0.1em; }

    .form-footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
      margin-top: 32px;
      padding-top: 20px;
      border-top: 1px solid var(--border-color);
    }
  `]
})
export class ProductFormComponent implements OnInit {
  isEditMode = false;
  productId: string | null = null;
  saving = false;
  uploadingImages = false;
  isMobileCategory = false;

  categories: Category[] = [];
  collections: Collection[] = [];
  deviceModels: DeviceModelItem[] = [];
  materials: MaterialItem[] = [];
  brands: BrandItem[] = [];
  brandGroups: BrandGroup[] = [];
  availableSubCategories: SubCategory[] = [];
  availableCollections: Collection[] = [];
  selectedGroupModels: string[] = [];

  product: Partial<Product> = {
    title: '',
    price: null as any,
    slashPrice: null as any,
    category: '',
    collectionName: '',
    subCategory: '',
    brandGroup: '',
    brandGroups: [],
    material: '',
    materials: [],
    productType: '',
    typeLabel: '',
    optionLabel: '',
    variants: [],
    frames: [],
    deviceModels: [],
    images: [],
    badge: '',
    bg: '#18181B',
    label: '',
    icon: '',
    imageUrl: '',
    cloudinaryId: '',
    description: '',
    inStock: true,
    featured: false
  };

  constructor(
    private productService: ProductService,
    private categoryService: CategoryService,
    private collectionService: CollectionService,
    private deviceModelService: DeviceModelService,
    private materialService: MaterialService,
    private brandService: BrandService,
    private brandGroupService: BrandGroupService,
    private subCategoryService: SubCategoryService,
    private uploadService: UploadService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadCollections();
    this.loadDeviceModels();
    this.loadMaterials();
    this.loadBrands();
    this.loadBrandGroups();

    this.productId = this.route.snapshot.paramMap.get('id');
    if (this.productId) {
      this.isEditMode = true;
      this.loadProduct(this.productId);
    } else {
      this.isEditMode = false;
      this.resetProductForm();
    }
  }

  resetProductForm(): void {
    this.product = {
      title: '',
      price: null as any,
      slashPrice: null as any,
      category: '',
      collectionName: '',
      subCategory: '',
      brandGroup: '',
      brandGroups: [],
      material: '',
      materials: [],
      productType: '',
      typeLabel: '',
      optionLabel: '',
      variants: [],
      frames: [],
      deviceModels: [],
      images: [],
      badge: '',
      bg: '#18181B',
      label: '',
      icon: '',
      imageUrl: '',
      cloudinaryId: '',
      description: '',
      inStock: true,
      featured: false
    };
    this.selectedGroupModels = [];
    this.availableSubCategories = [];
  }

  loadCategories(): void {
    this.categoryService.getCategories().subscribe({
      next: (res) => {
        this.categories = res;
        if (this.product.category) {
          this.checkMobileCategory(this.product.category);
          this.loadSubCategoriesForCategory(this.product.category);
        }
      },
      error: (err) => console.error(err)
    });
  }

  loadCollections(): void {
    this.collectionService.getCollections().subscribe({
      next: (res) => {
        this.collections = res;
        if (this.availableCollections.length === 0) {
          this.availableCollections = res;
        }
      },
      error: (err) => console.error(err)
    });
  }

  loadDeviceModels(): void {
    this.deviceModelService.getDeviceModels().subscribe({
      next: (res) => (this.deviceModels = res),
      error: (err) => console.error(err)
    });
  }

  loadMaterials(): void {
    this.materialService.getMaterials().subscribe({
      next: (res) => (this.materials = res),
      error: (err) => console.error(err)
    });
  }

  loadBrands(): void {
    this.brandService.getBrands().subscribe({
      next: (res) => (this.brands = res),
      error: (err) => console.error(err)
    });
  }

  loadBrandGroups(): void {
    this.brandGroupService.getBrandGroups().subscribe({
      next: (res) => (this.brandGroups = res),
      error: (err) => console.error(err)
    });
  }

  loadProduct(id: string): void {
    this.productService.getProductById(id).subscribe({
      next: (data) => {
        this.product = data;
        if (!this.product.variants) this.product.variants = [];
        if (!this.product.frames) this.product.frames = [];
        if (!this.product.productType) this.product.productType = '';
        if (!this.product.deviceModels) this.product.deviceModels = [];
        if (!this.product.materials) {
          this.product.materials = this.product.material ? [this.product.material] : [];
        }
        if (!this.product.brandGroups) {
          this.product.brandGroups = this.product.brandGroup ? [this.product.brandGroup] : [];
        }
        if (!this.product.images) this.product.images = [];
        this.checkMobileCategory(this.product.category || '');
        if (this.product.category) {
          this.loadSubCategoriesForCategory(this.product.category);
          this.loadCollectionsForSelection(this.product.category, this.product.subCategory);
        } else {
          this.availableCollections = this.collections;
        }
        if ((this.product.brandGroups || []).length > 0) {
          this.syncBrandGroupsAndModels();
        } else if (this.product.brandGroup) {
          this.product.brandGroups = [this.product.brandGroup];
          this.syncBrandGroupsAndModels();
        }
      },
      error: (err) => alert('Failed to load product: ' + err.message)
    });
  }

  onCategoryChange(slug: string): void {
    this.checkMobileCategory(slug);
    this.product.subCategory = '';
    this.product.collectionName = '';
    this.loadSubCategoriesForCategory(slug);
    this.loadCollectionsForSelection(slug, '');

    // Pre-fill the type from the category's Mobile flag if the admin hasn't chosen one
    if (!this.product.productType) {
      this.product.productType = this.isMobileCategory ? 'phone-case' : 'other';
    }
  }

  onSubCategoryChange(subName: string): void {
    this.product.collectionName = '';
    const selectedSub = this.availableSubCategories.find(s => s.name === subName || s.slug === subName);
    const subParam = selectedSub ? selectedSub.slug : subName;
    this.loadCollectionsForSelection(this.product.category || '', subParam);
  }

  loadCollectionsForSelection(catSlug: string, subParam?: string): void {
    this.collectionService.getCollections(catSlug || undefined, subParam || undefined).subscribe({
      next: (cols) => {
        this.availableCollections = cols;
        // If empty when filtered by subcategory, fallback to collections for the category or all collections
        if (this.availableCollections.length === 0 && this.collections.length > 0) {
          this.availableCollections = this.collections.filter(c => !catSlug || c.category === catSlug || (!c.category && catSlug === 'mobile-cases'));
          if (this.availableCollections.length === 0) {
            this.availableCollections = this.collections;
          }
        }
      },
      error: (err) => {
        console.warn('Could not load collections:', err);
        this.availableCollections = this.collections;
      }
    });
  }

  checkMobileCategory(slug: string): void {
    if (!slug) {
      this.isMobileCategory = false;
      return;
    }
    const cat = this.categories.find(c => c.slug === slug);
    if (cat) {
      this.isMobileCategory = cat.isMobile !== false;
    } else {
      // Fallback: check if slug contains sleeve/mat/accessory
      this.isMobileCategory = !(slug.includes('sleeve') || slug.includes('mat') || slug.includes('bag') || slug.includes('apparel'));
    }
  }

  loadSubCategoriesForCategory(catSlug: string): void {
    if (!catSlug) {
      this.availableSubCategories = [];
      return;
    }
    this.subCategoryService.getSubCategories(catSlug).subscribe({
      next: (subs) => {
        this.availableSubCategories = subs;
      },
      error: (err) => console.warn('Could not load subcategories for', catSlug, err)
    });
  }

  isBrandGroupSelected(name: string): boolean {
    return (this.product.brandGroups || []).includes(name);
  }

  toggleBrandGroup(name: string): void {
    if (!this.product.brandGroups) this.product.brandGroups = [];
    const index = this.product.brandGroups.indexOf(name);
    if (index > -1) {
      this.product.brandGroups.splice(index, 1);
    } else {
      this.product.brandGroups.push(name);
    }
    this.syncBrandGroupsAndModels();
  }

  clearAllBrandGroups(): void {
    this.product.brandGroups = [];
    this.product.brandGroup = '';
    this.syncBrandGroupsAndModels();
  }

  syncBrandGroupsAndModels(): void {
    const selectedGroupNames = this.product.brandGroups || [];
    const activeGroups = this.brandGroups.filter(g => selectedGroupNames.includes(g.name));

    const merged = new Set<string>();
    activeGroups.forEach(g => {
      (g.deviceModels || []).forEach(m => merged.add(m));
    });

    this.product.deviceModels = Array.from(merged);
    this.selectedGroupModels = Array.from(merged);
    this.product.brandGroup = selectedGroupNames.length > 0 ? selectedGroupNames[0] : '';
  }

  getGroupsForBrand(brandSlug: string): BrandGroup[] {
    return this.brandGroups.filter(g => g.brandSlug === brandSlug && g.active !== false);
  }

  onBrandGroupChange(groupIdOrName: string): void {
    this.updateGroupModels(groupIdOrName);
  }

  updateGroupModels(groupIdOrName: string): void {
    if (!groupIdOrName) {
      this.selectedGroupModels = [];
      return;
    }
    const group = this.brandGroups.find(g => g._id === groupIdOrName || g.name === groupIdOrName || g.slug === groupIdOrName);
    this.selectedGroupModels = group ? group.deviceModels : [];
    if (group) {
      this.product.deviceModels = [...group.deviceModels];
      this.product.brandGroup = group.name;
      this.product.brandGroups = [group.name];
    }
  }

  isDeviceModelSelected(name: string): boolean {
    return (this.product.deviceModels || []).includes(name);
  }

  toggleDeviceModel(name: string): void {
    if (!this.product.deviceModels) this.product.deviceModels = [];
    const index = this.product.deviceModels.indexOf(name);
    if (index > -1) {
      this.product.deviceModels.splice(index, 1);
    } else {
      this.product.deviceModels.push(name);
    }
  }

  isMaterialSelected(name: string): boolean {
    return (this.product.materials || []).includes(name);
  }

  toggleMaterial(name: string): void {
    if (!this.product.materials) this.product.materials = [];
    const index = this.product.materials.indexOf(name);
    if (index > -1) {
      this.product.materials.splice(index, 1);
    } else {
      this.product.materials.push(name);
    }
    // Synchronize primary material
    this.product.material = this.product.materials.length > 0 ? this.product.materials[0] : '';
  }

  selectAllMaterials(): void {
    this.product.materials = this.materials.map(m => m.name);
    this.product.material = this.product.materials[0] || '';
  }

  clearAllMaterials(): void {
    this.product.materials = [];
    this.product.material = '';
  }

  onImageUploaded(data: { url: string; cloudinaryId: string }): void {
    this.product.imageUrl = data.url;
    this.product.cloudinaryId = data.cloudinaryId;
  }

  onImageRemoved(): void {
    this.product.imageUrl = '';
    this.product.cloudinaryId = '';
  }

  onMultiImageSelected(event: Event): void {
    const files = Array.from((event.target as HTMLInputElement).files || []);
    if (files.length === 0) return;

    const existing = this.product.images || [];
    const remaining = 6 - existing.length;
    const toUpload = files.slice(0, remaining);

    this.uploadingImages = true;
    this.uploadService.uploadMultipleImages(toUpload).subscribe({
      next: (res) => {
        this.uploadingImages = false;
        const newImages: ProductImage[] = res.images.map((img, idx) => ({
          url: img.url,
          cloudinaryId: img.cloudinaryId,
          order: existing.length + idx
        }));
        this.product.images = [...existing, ...newImages];
        // Set primary imageUrl from first image
        if (!this.product.imageUrl && this.product.images.length > 0) {
          this.product.imageUrl = this.product.images[0].url;
          this.product.cloudinaryId = this.product.images[0].cloudinaryId;
        }
      },
      error: (err) => {
        this.uploadingImages = false;
        alert('Failed to upload images: ' + (err.error?.message || err.message));
      }
    });
  }

  removeProductImage(index: number): void {
    if (!this.product.images) return;
    this.product.images.splice(index, 1);
    // Re-order
    this.product.images.forEach((img, i) => (img.order = i));
    // Update primary
    if (index === 0) {
      this.product.imageUrl = this.product.images.length > 0 ? this.product.images[0].url : '';
      this.product.cloudinaryId = this.product.images.length > 0 ? this.product.images[0].cloudinaryId : '';
    }
  }

  get supportsSizeOptions(): boolean {
    return ['poster', 'desk-mat', 'other'].includes(this.product.productType || '');
  }

  trackByIndex(index: number): number {
    return index;
  }

  addVariant(): void {
    if (!this.product.variants) this.product.variants = [];
    this.product.variants.push({
      name: '',
      price: null as any,
      slashPrice: null as any,
      specs: '',
      inStock: true,
      order: this.product.variants.length
    });
  }

  addFrame(): void {
    if (!this.product.frames) this.product.frames = [];
    this.product.frames.push({
      name: '',
      priceAdjustment: 0,
      imageUrl: '',
      inStock: true,
      order: this.product.frames.length
    });
  }

  removeFrame(index: number): void {
    this.product.frames?.splice(index, 1);
  }

  onFrameImageSelected(event: Event, frame: ProductFrame): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;
    this.uploadService.uploadImage(file).subscribe({
      next: (res) => {
        frame.imageUrl = res.imageUrl;
        input.value = '';
      },
      error: () => alert('Frame image upload failed. Please try again.')
    });
  }

  removeVariant(index: number): void {
    this.product.variants?.splice(index, 1);
  }

  onSubmit(): void {
    // Sizes/frames only apply to size-based types; drop them otherwise so checkout isn't affected
    if (!this.supportsSizeOptions) {
      if ((this.product.variants || []).length > 0 || (this.product.frames || []).length > 0) {
        if (!confirm('Sizes/frames are only used for Art poster, Desk mat or Other. They will be removed from this product. Continue?')) {
          return;
        }
      }
      this.product.variants = [];
      this.product.frames = [];
    }

    // Labels only apply to "Other" products
    if (this.product.productType !== 'other') {
      this.product.typeLabel = '';
      this.product.optionLabel = '';
    }

    const frames = this.product.frames || [];
    const frameNames = new Set<string>();
    for (const f of frames) {
      const name = (f.name || '').trim();
      if (!name) {
        alert('Every frame needs a name (or remove the empty row).');
        return;
      }
      if (!(Number(f.priceAdjustment) >= 0)) {
        f.priceAdjustment = 0;
      }
      if (frameNames.has(name.toLowerCase())) {
        alert(`Duplicate frame "${name}". Each frame name must be unique.`);
        return;
      }
      frameNames.add(name.toLowerCase());
    }

    const variants = this.product.variants || [];
    if (variants.length > 0) {
      const names = new Set<string>();
      for (const v of variants) {
        const name = (v.name || '').trim();
        if (!name || !(Number(v.price) > 0)) {
          alert('Every size needs a name and a price greater than 0 (or remove the empty row).');
          return;
        }
        if (names.has(name.toLowerCase())) {
          alert(`Duplicate size "${name}". Each size name must be unique.`);
          return;
        }
        names.add(name.toLowerCase());
      }
      // Base price mirrors the cheapest size (the server does the same)
      const minFrameExtra = frames.length ? Math.min(...frames.map(f => Number(f.priceAdjustment) || 0)) : 0;
      this.product.price = Math.min(...variants.map(v => Number(v.price))) + minFrameExtra;
    }

    if (!this.product.title || !this.product.price || !this.product.category) {
      alert('Please fill out all required fields (Title, Price, Category)');
      return;
    }

    // If non-mobile collection, do not persist mobile models or brand group
    if (!this.isMobileCategory) {
      this.product.deviceModels = [];
      this.product.brandGroup = '';
      this.product.brandGroups = [];
      this.product.brandGroupIds = [];
    }

    // Attach relational IDs for production grade linking
    const selectedCat = this.categories.find(c => c.slug === this.product.category || c._id === this.product.categoryId);
    if (selectedCat) {
      this.product.categoryId = selectedCat._id;
      this.product.category = selectedCat.slug;
    }

    if (this.product.collectionName) {
      const selectedCol = this.collections.find(c => c.slug === this.product.collectionName || c.name === this.product.collectionName);
      if (selectedCol) {
        this.product.collectionId = selectedCol._id;
      }
    }

    if (this.product.subCategory) {
      const selectedSub = this.availableSubCategories.find(s => s.name === this.product.subCategory || s.slug === this.product.subCategory);
      if (selectedSub) {
        this.product.subCategoryId = selectedSub._id;
      }
    }

    if ((this.product.brandGroups || []).length > 0) {
      const matchingGroups = this.brandGroups.filter(bg => (this.product.brandGroups || []).includes(bg.name));
      this.product.brandGroupIds = matchingGroups.map(bg => bg._id).filter((id): id is string => Boolean(id));
    }

    this.saving = true;

    if (this.isEditMode && this.productId) {
      this.productService.updateProduct(this.productId, this.product).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/products']);
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to update product: ' + err.message);
        }
      });
    } else {
      this.productService.createProduct(this.product).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/products']);
        },
        error: (err) => {
          this.saving = false;
          alert('Failed to create product: ' + err.message);
        }
      });
    }
  }
}
